import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { NgForm } from "@angular/forms";
import { BehaviorSubject } from "rxjs";
import { InsightPassengerAddressModel } from "src/app/core/models/individual-model/insight-passenger-address.model";
import { AddressTypeReferenceModel, CountryReferenceModel, LanguageReferenceModel } from "src/app/core/models/reference-model/reference-general-model";
import { Select2Data } from "src/app/shared/ui/forms/inputs/oops-select2";
import { AddressMapperService } from "../shared/address-mapper.service";
import { AddressView } from "../shared/address-view";
import { RouteDataService } from "../shared/route-data-service";
import { select2AddressType, select2Country, select2LocationGroupOption, select2UseAddress } from "../shared/select2-configuration";
import { LocationReference } from "src/app/core/models/location-model";
import { CountryReferenceService, LocationReferenceService } from "src/app/core/services/system-services";
import { StringHelperService } from "src/app/core/utils/string-helper.service";

declare var $: any;

@Component({
    selector: 'op-insight-address-detail',
    templateUrl: './address-detail.component.html',
    providers: [RouteDataService]   
})
export class InsightAddressDetailComponent implements OnInit, OnChanges {
    readonly ERROR_ADDRESS_TYPE_REQUIRED = 'Address Type is required.'
    readonly ERROR_CITY_REQUIRED = 'City is required.'
    readonly ERROR_COUNTRY_REQUIRED = 'Country is required.'
    readonly ERROR_POSTAL_CODE_REQUIRED = 'Postal Code is required.'

    public addressTypeOption: any;
    public useAddressOption: any;
    public countryOption: any;
    public locationGroupOption: any;
    public processing: boolean = false;
    public selectedUseAddress: boolean = false;
    public locations$ = new BehaviorSubject<Select2Data[]>(null);
    
    private useAddresses: InsightPassengerAddressModel[]; 

    private _locationReferences: LocationReference[];
    private _countryReferences: CountryReferenceModel[];
    
    private _defaultCountry: Select2Data;
    private _defaultLocation: Select2Data;

    public checkBoxId = this.stringHelperService.NewGuid()

    @Input() insightAddress = new AddressView(); 
    @Input() addressTypeReferences: AddressTypeReferenceModel[];
    @Input() countryReferences: CountryReferenceModel[];   
    @Input() languageReferences: LanguageReferenceModel[];
    @Input() useAddressDropdown: InsightPassengerAddressModel[];
    @Output() onUpdateFormStatus = new EventEmitter<boolean>();

    @ViewChild("locationAddressSelect2") locationAddressSelect2: ElementRef;
    @ViewChild('addressDetailForm') addressDetailForm: NgForm;

    get isNullOrganisationId() : boolean {
        if (this.insightAddress.organisationId) {
            return true;
        }

        return false;
    }

    constructor(private routeDataService: RouteDataService,
        private addressMapperService: AddressMapperService,
        private locationReferenceService: LocationReferenceService,
        private countryReferenceService: CountryReferenceService,
        private changeDetectionRef: ChangeDetectorRef,
        private stringHelperService: StringHelperService) {
        this.setOptionControl();
        this.getLocationReferences();
        this.getCountryReferences();
    }

    ngOnInit(): void {
        this.locationGroupOption.minimumInputLength = 0;
        this.locationGroupOption.ajax = this.getLocationReferenceAjax();
        this.countryOption.ajax = this.getCountryReferenceAjax();

        if (this.insightAddress.useAddressId) {
            this.useAddressChange(this.insightAddress.useAddressId);
        }
    }

    private getLocationReferenceAjax() {
        const populateData = (locationReferences: LocationReference[], searchTerms: string) => {
            const locationReferencesSorted = locationReferences
                ?.filter(l => l.locationName.toLowerCase().includes(searchTerms) ||
                    l.locationCode.toLowerCase().includes(searchTerms));
            return locationReferencesSorted
                ?.map(x => new Select2Data(x.locationId, x.locationName));;
        }
        return {
            delay: 200,
            transport: (params, success) => {
                const countryCode = this.insightAddress.countryCode;
                const searchTerms = params.data?.term?.toLowerCase() ?? '';
                if (countryCode) {
                    this.locationReferenceService.getLocationReferenceSearch({
                        statusCode: 'A',
                        countryCode: countryCode,
                        locationNameOrCode: searchTerms
                    }).subscribe(
                        responses => {
                            const sorted = responses
                                ?.sort((a, b) => a.locationName.localeCompare(b.locationName))
                            success({
                                results: sorted
                                    ?.map(x => new Select2Data(x.id, x.locationName))
                            })
                        }
                    )
                } else {
                    success({
                        results: populateData(this._locationReferences, searchTerms)
                    })
                }
            }
        }
    }

    private getCountryReferenceAjax() {
        const populateData = (searchTerms: string) => {
            const locationId = this.insightAddress?.locationId;
            const location = this._locationReferences?.find(l => l.locationId == locationId);
            return this.countryReferences
                ?.filter(x => x.countryCode.toLowerCase().includes(searchTerms.toLowerCase()) ||
                    x.countryName.toLowerCase().includes(searchTerms.toLowerCase()))
                ?.filter(x => !location || (location.countryCode == x.countryCode))
                ?.map(x => new Select2Data(x.countryCode, x.countryName))
        }
        return {
            delay: 200,
            transport: (params, success) => {
                const searchTerm = params?.data?.term ?? '';
                success({
                    results: populateData(searchTerm)
                })
            }
        }
    }

    private setOptionControl() {     
        this.addressTypeOption = select2AddressType;
        this.useAddressOption = select2UseAddress;
        this.countryOption = select2Country;
        this.locationGroupOption = select2LocationGroupOption;
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.setSelectedLocation(this.insightAddress);
    }

    private getLocationReferences() {
        this.locationReferenceService.getLocationReferenceList()
            .subscribe(
                (response) => {
                    this._locationReferences = response
                        ?.sort((a, b) => a.locationName.localeCompare(b.locationName));
                }
            )
    }    

    private getCountryReferences() {
        this.countryReferenceService.getCountryReference()
            .subscribe(
                (response) => {
                    this._countryReferences = response
                        ?.sort((a, b) => a.countryName.localeCompare(b.countryName));
                }
            )
    }
    
    getInsightAddress() : AddressView {
        if (!this.isValidForm()) {
            return null;
        }
        return this.insightAddress
    }

    setProcessing(flag: boolean) {
        this.processing = flag;
        this.changeDetectionRef.detectChanges();
    }

    public isValidForm() : boolean {
        this.processing = true;
        if(!this.addressDetailForm.valid && this.addressDetailForm.disabled != true) {
            this.onUpdateFormStatus.emit(false);
            return false;
        }

        this.processing = false; 
        this.onUpdateFormStatus.emit(true);
        return true;
    }

    getErrorMessageForm(): string {
        if (this.addressDetailForm.valid) {
            return 
        }

        if (!this.addressDetailForm.controls['addressTypeCode'].value) {
            return this.ERROR_ADDRESS_TYPE_REQUIRED;
        } else if (!this.addressDetailForm.controls['city'].value) {
            return this.ERROR_CITY_REQUIRED;
        } else if (!this.addressDetailForm.controls['postalCode'].value) {
            return this.ERROR_POSTAL_CODE_REQUIRED;
        } else if (!this.addressDetailForm.controls['countryCode'].value) {
            return this.ERROR_COUNTRY_REQUIRED;
        }
    }

    public setSelectedLocation(address: AddressView) {
        if (address.locationId) {
            let valueReturn: Select2Data[] = new Array();
            let data = new Select2Data(address.locationId, address.locationName);
            valueReturn.push(data);
            this.locations$.next(valueReturn);
        }
        
        this.locations$.next(null);
    }

    public addressLocationChange(value) {
        if (this.insightAddress.countryCode) return;

        const locationId = value;
        const location = this._locationReferences?.find(l => l.locationId == locationId);
        let countryCodeRef = this.countryReferences
            ?.find(x => !location || (location.countryCode == x.countryCode));
        
        this.insightAddress.countryCode = countryCodeRef?.countryCode;
    }

    public addressTypeChange(value) {
        this.insightAddress.addressTypeCode = value;
        this.insightAddress.addressTypeName = this.addressTypeReferences.filter(it => it.addressTypeCode == value)[0]?.addressTypeName ?? null;
    }

    public addressCountryChange(value) {
        this.insightAddress.countryCode = value;
        this.insightAddress.countryName = this.countryReferences.filter(it => it.countryCode == value)[0]?.countryName ?? null;

        this.insightAddress.locationId = null;
    }

    public useAddressChange(value) {
        if (value) {
            this.useAddresses = this.useAddressDropdown;
            var addressRef = this.useAddresses.filter(it => it.addressId == value)[0];
            this.insightAddress.addressLine1 = addressRef.addressLine1;
            this.insightAddress.addressLine2 = addressRef.addressLine2;
            this.insightAddress.state = addressRef.state;
            this.insightAddress.postalCode = addressRef.postalCode;
            this.insightAddress.province = addressRef.province;
            this.insightAddress.city = addressRef.city;
            this.insightAddress.countryCode = addressRef.countryCode;
            this.insightAddress.countryName = addressRef.countryName;
            this.insightAddress.locationId = addressRef.locationId;
            this.insightAddress.locationName = addressRef.locationName;
            this.insightAddress.locationDataSave = this.addressMapperService.getLocationSave(addressRef.locationId, addressRef.locationName);
            this.insightAddress.useAddressId = addressRef.useAddressId ?? addressRef?.addressId;
            this.setSelectedLocation(this.insightAddress);

            this.selectedUseAddress = true;
        }
        else {
            this.insightAddress.useAddressId = null;
            this.selectedUseAddress = false;
        }
    }

    disableEditUseAddress() {
        if (this.selectedUseAddress || this.insightAddress.organisationId) {
            return true;
        }
        return false;
    }

    get filteredUseAddress(): InsightPassengerAddressModel[] {
        if (this.insightAddress?.addressId && this.useAddressDropdown) {
            return this.useAddressDropdown.filter(address => address.addressId != this.insightAddress?.addressId);
        }
        return this.useAddressDropdown;
    }

    getCountryReference(countryCode: string): Select2Data {
        if (!countryCode) return null;
        if (this._defaultCountry?.id == countryCode) return this._defaultCountry;
        this._defaultCountry = this._countryReferences
            ?.filter(x => x.countryCode == countryCode)
            ?.map(x => new Select2Data(x.countryCode, x.countryName))
            ?.[0];
        return this._defaultCountry;
    }

    getLocationReference(locationId: string): Select2Data {
        if (!locationId) return null;
        if (this._defaultLocation?.id == locationId) return this._defaultLocation;
        this._defaultLocation = this._locationReferences
            ?.filter(x => x.locationId == locationId)
            ?.map(x => new Select2Data(x.locationId, x.locationName))
            ?.[0];
        return this._defaultLocation;
    }
}